import { useState, useMemo } from 'react';
import { Input } from "@/components/ui/input";
import EventLogger from '@/components/EventLogger';
import { useAppSelector } from '@/hooks/useAppSelector';
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const Test = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const quarterlyEvents = useAppSelector(state => state.quarterlyEvents.events);
  const supaEvents = useAppSelector(state => state.supaEvents.events);
  const companies = useAppSelector(state => state.supaCompanies.companies);

  // Filter events for 2024 price changes
  const priceEvents = useMemo(() => {
    return quarterlyEvents.filter(event => {
      const isYear2024 = event.quarter.startsWith('2024');
      return isYear2024 && (event.price_increased || event.price_decreased);
    });
  }, [quarterlyEvents]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return supaEvents
      .map(event => ({
        name: `${event.slug} (${event.quarter})`,
        average: event.average || 0,
        color: (event.average || 0) > 0 ? '#22c55e' : '#ef4444'
      }))
      .sort((a, b) => a.average - b.average); // Sort by average from smallest to largest
  }, [supaEvents]);

  // Prepare category averages data with separate increase/decrease
  const categoryAveragesData = useMemo(() => {
    const categoryMap = new Map<string, {
      increaseTotal: number;
      increaseCount: number;
      decreaseTotal: number;
      decreaseCount: number;
    }>();

    supaEvents.forEach(event => {
      if (event.average !== null) {
        const company = companies.find(c => c.slug === event.slug);
        const category = company?.category || 'Unknown';

        const existing = categoryMap.get(category) || {
          increaseTotal: 0,
          increaseCount: 0,
          decreaseTotal: 0,
          decreaseCount: 0
        };

        if (event.average > 0) {
          categoryMap.set(category, {
            ...existing,
            increaseTotal: existing.increaseTotal + event.average,
            increaseCount: existing.increaseCount + 1
          });
        } else if (event.average < 0) {
          categoryMap.set(category, {
            ...existing,
            decreaseTotal: existing.decreaseTotal + event.average,
            decreaseCount: existing.decreaseCount + 1
          });
        }
      }
    });

    const data = Array.from(categoryMap.entries())
      .map(([category, data]) => ({
        name: category,
        increaseAverage: data.increaseCount > 0 ? data.increaseTotal / data.increaseCount : 0,
        decreaseAverage: data.decreaseCount > 0 ? data.decreaseTotal / data.decreaseCount : 0,
        average: (data.increaseTotal + data.decreaseTotal) / (data.increaseCount + data.decreaseCount)
      }));

    // Split and sort the data for increases and decreases separately
    const increasesData = data
      .filter(item => item.increaseAverage > 0)
      .sort((a, b) => a.increaseAverage - b.increaseAverage);

    const decreasesData = data
      .filter(item => item.decreaseAverage < 0)
      .sort((a, b) => a.decreaseAverage - b.decreaseAverage);

    return { increasesData, decreasesData };
  }, [supaEvents, companies]);

  // Get events for selected category
  const selectedCategoryEvents = useMemo(() => {
    if (!selectedCategory) return [];

    return supaEvents
      .filter(event => {
        const company = companies.find(c => c.slug === event.slug);
        return company?.category === selectedCategory;
      })
      .map(event => ({
        slug: event.slug,
        quarter: event.quarter,
        average: event.average || 0
      }))
      .sort((a, b) => b.average - a.average); // Sort by average descending

  }, [selectedCategory, supaEvents, companies]);

  const currentEvent = priceEvents[currentIndex];

  const handleNext = () => {
    if (currentIndex < priceEvents.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const formatYAxis = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const isIncrease = payload[0].dataKey === 'increaseAverage';
      return (
        <div className="bg-white p-4 border rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          <p className={`text-${isIncrease ? 'green' : 'red'}-600`}>
            {(Math.abs(value) * 100).toFixed(1)}% {isIncrease ? 'increase' : 'decrease'}
          </p>
        </div>
      );
    }
    return null;
  };

  const handleBarClick = (data: any) => {
    console.log('Bar clicked:', data);
    if (data && data.activePayload && data.activePayload[0]) {
      setSelectedCategory(data.activePayload[0].payload.name);
    }
  };

  const handleDownloadIncreases = () => {
    try {
      // Create CSV headers
      const headers = ['Category', 'Average Increase (%)'];

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...categoryAveragesData.increasesData.map(item => [
          item.name,
          (item.increaseAverage * 100).toFixed(1)
        ].join(','))
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `category-price-increases-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Category price increases exported to CSV');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export category price increases');
    }
  };

  return (
    <div className="container py-8">
      <div className="space-y-6">
        {/* Individual Price Changes Chart */}
        <div className="border p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Individual Price Changes</h2>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis 
                  tickFormatter={formatYAxis}
                  label={{ 
                    value: 'Price Change (%)', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' }
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="average"
                  fill="#000000"
                  radius={[4, 4, 0, 0]}
                  fillOpacity={0.8}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Price Increases Chart */}
        <div className="border p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Price Increases by Category</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadIncreases}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={categoryAveragesData.increasesData}
                margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
                onClick={handleBarClick}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis 
                  tickFormatter={formatYAxis}
                  label={{ 
                    value: 'Average Price Increase (%)', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' }
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="increaseAverage"
                  fill="#22c55e"
                  radius={[4, 4, 0, 0]}
                  fillOpacity={0.8}
                  cursor="pointer"
                  name="Price Increases"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Price Decreases Chart */}
        <div className="border p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Price Decreases by Category</h2>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={categoryAveragesData.decreasesData}
                margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
                onClick={handleBarClick}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis 
                  tickFormatter={formatYAxis}
                  label={{ 
                    value: 'Average Price Decrease (%)', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' }
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="decreaseAverage"
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                  fillOpacity={0.8}
                  cursor="pointer"
                  name="Price Decreases"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Details Table */}
        {selectedCategory && (
          <div className="border p-4 rounded-lg">
            <h2 className="text-xl font-semibold mb-4">
              Details for {selectedCategory}
            </h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Quarter</TableHead>
                  <TableHead>Price Change</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedCategoryEvents.map((event, index) => (
                  <TableRow key={`${event.slug}-${event.quarter}`}>
                    <TableCell className="font-medium">{event.slug}</TableCell>
                    <TableCell>{event.quarter}</TableCell>
                    <TableCell className={event.average > 0 ? 'text-green-600' : 'text-red-600'}>
                      {(event.average * 100).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Event Details Section */}
        <div className="space-y-4 border p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">2024 Price Events</h2>
            <div className="text-sm text-gray-600">
              Total Events: {priceEvents.length}
            </div>
          </div>

          {priceEvents.length > 0 ? (
            <>
              <div className="flex items-center justify-between gap-4">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Event {currentIndex + 1} of {priceEvents.length}
                </span>
                <Button
                  variant="outline"
                  onClick={handleNext}
                  disabled={currentIndex === priceEvents.length - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Slug</label>
                    <Input
                      value={currentEvent?.slug || ''}
                      readOnly
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Quarter</label>
                    <Input
                      value={currentEvent?.quarter || ''}
                      readOnly
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <EventLogger 
                slug={currentEvent?.slug || ''} 
                quarter={currentEvent?.quarter || ''} 
              />
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No price events found for 2024
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Test;
